export const mergeTableColumnsWithDefaults = <T extends { key: string }>(currentColumns: T[], defaultColumns: T[]) => {
    const defaultColumnsByKey = new Map(defaultColumns.map(column => [column.key, column]));
    const currentColumnKeySet = new Set(currentColumns.map(column => column.key));

    const mergedCurrentColumns = currentColumns.flatMap(column => {
        const defaultColumn = defaultColumnsByKey.get(column.key);

        return defaultColumn ? [{ ...defaultColumn, ...column }] : [];
    });

    const missingDefaultColumns = defaultColumns.filter(column => !currentColumnKeySet.has(column.key));

    return [...mergedCurrentColumns, ...missingDefaultColumns];
};

export const reorderTableColumns = <T extends { key: string }>(columns: T[], columnOrder: string[]) => {
    const columnsByKey = new Map(columns.map(column => [column.key, column]));
    const reorderedColumns = columnOrder.flatMap(columnKey => {
        const column = columnsByKey.get(columnKey);

        return column ? [column] : [];
    });
    const reorderedColumnKeySet = new Set(reorderedColumns.map(column => column.key));
    const remainingColumns = columns.filter(column => !reorderedColumnKeySet.has(column.key));

    return [...reorderedColumns, ...remainingColumns];
};

export type PersistedTableColumn<T extends string> = {
    key: T;
    visible: boolean;
    width?: number | string;
};

export const applyPersistedTableColumns = <T extends { key: string; visible: boolean; width?: number | string }>(
    defaultColumns: T[],
    persistedColumns?: PersistedTableColumn<T['key']>[]
) => {
    if (!persistedColumns?.length) {
        return defaultColumns;
    }

    const persistedColumnsByKey = new Map(persistedColumns.map(column => [column.key, column]));
    const reorderedColumns = reorderTableColumns(
        defaultColumns,
        persistedColumns.map(column => column.key)
    );

    return reorderedColumns.map(column => {
        const persistedColumn = persistedColumnsByKey.get(column.key);

        return persistedColumn
            ? {
                  ...column,
                  visible: persistedColumn.visible,
                  width: persistedColumn.width,
              }
            : column;
    });
};

export const serializePersistedTableColumns = <T extends { key: string; visible: boolean; width?: number | string }>(
    columns: T[]
): PersistedTableColumn<T['key']>[] =>
    columns.map(({ key, visible, width }) =>
        width === undefined
            ? {
                  key,
                  visible,
              }
            : {
                  key,
                  visible,
                  width,
              }
    );
